<template>
    <EditProductDialog
        v-if="product"
        :company-id="companyId"
        :product-id="productId"
        is-master-template
        is-billing-product-edit
        :billing-item="product"
        @save="confirmSave($event)"
        @updateAll="updateAll($event)"
        @close_modal="$emit('close_modal')"
        @updateErrors="onUpdateErrors"
    >
        <template #footer>
            <v-btn
                v-if="productId && canEdit"
                text
                :loading="loader.isLoading"
                color="error"
                @click="onDeleteProductClick"
            >
                {{ $t("Delete") }}
            </v-btn>
        </template>
    </EditProductDialog>
</template>

<script>
import Vue from "vue";
import {computed, onMounted, reactive, ref} from "@vue/composition-api";
import EditProductDialog from "@/views/Dialog/EditProductDialog/EditProductDialog.vue";
import Loader from "@/utility/loader";
import ProductModule from "@/store/modules/product";
import {$t} from "@/i18n";
import DialogModule from "@/store/modules/dialog";
import {formatMoneyNumber, formatNumber, formatSysDate} from "@/plugins/formatting";
import AppModule from "@/store/modules/app";
import AccountModule from "@/store/modules/account";
import RightsModule from "@/composables/use-rights";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import {deepClone} from "@/helpers/helpers";

export default {
    name: "EditCompanyProductDialog",
    components: {
        EditProductDialog
    },
    props: {
        companyId: {
            type: Number,
            required: true
        },
        productId: {
            type: Number,
            default: null
        }
    },
    setup(props, ctx) {
        const {
            dialogs,
            closeDialog,
            setConfirmOptions,
            openConfirmDialog
        } = DialogModule();
        const {getAccount} = AccountModule();
        const {can_edit_products} = RightsModule();
        const {clearValidationErrors, handleErrorResponse, languages, fetchClassifiers} = AppModule();
        const {saveProduct, deleteProduct, fetchProduct} = ProductModule();
        const loader = reactive(new Loader())
        const product = ref(null)
        const errors = ref({})

        const canEdit = computed(() => {
            const account = getAccount(props.companyId);
            return can_edit_products(account.value)
        })

        const defaultNames = computed(() => {
            let names = {}

            for (const language of languages.value) {
                names[language.code] = ""
            }

            return names
        })

        const getProductDefaults = () => {
            let default_date_from = formatSysDate(new Date((new Date().getFullYear() - 1), 0, 1))
            return Object.assign(
                {},
                {
                    id: 0,
                    account_id: props.companyId,
                    type: "fixed_price",
                    unit: "month",
                    topics: [],
                    pricelist_type: "fixed",
                    billable_filter: "",
                    discount_rate: null,
                    invoice_time_min: "",
                    invoice_quantity_min: "",
                    name: "",
                    bill_by: null,

                    tax_id: null,
                    article_id: null,
                    resource_id: null,
                    billing_project_id: null,
                    cost_center_id: null,

                    ext_variable: "",
                    translations: {"names": defaultNames.value},

                    periods: [
                        {
                            date_from: default_date_from,
                            prices: [
                                {
                                    range_from: 0,
                                    range_until: null,
                                    net_price: null,
                                    unit: "month",
                                    quantity: 1
                                }
                            ]
                        }
                    ],
                }
            );
        };

        const setupProduct = async () => {
            let productData
            if (props.productId) {
                // notice :: load product
                productData = await loadProduct(props.productId)
            } else {
                productData = getProductDefaults()
            }

            product.value = Object.assign({}, productData);
            if (!product.value.topics) product.value.topics = [];
        }

        onMounted(async () => {
            try {
                await fetchClassifiers({types: ["languages"]})
                await setupProduct()
            } catch (ex) {
                errorDialog.showError(ex);
            }
        });

        const loadProduct = async (product_id) => {
            // fetch
            const upd_product = await fetchProduct(product_id);
            // set
            for (let period of upd_product.periods) {
                for (let product_price of period.prices) {
                    product_price = Object.assign(product_price, {
                        range_from_f: product_price.range_from !== null ? formatNumber(product_price.range_from) : "",
                        range_until_f: product_price.range_until !== null ? formatNumber(product_price.range_until) : "",
                        net_price_f: product_price.net_price !== null ? formatMoneyNumber(product_price.net_price) : ""
                    });
                }
            }
            // ensure names
            if (!upd_product.translations) upd_product.translations = {};
            if (!upd_product.translations.names) upd_product.translations.names = {};
            for (let key of Object.keys(defaultNames.value)) {
                if (!upd_product.translations.names[key]) upd_product.translations.names[key] = defaultNames.value[key];
            }
            return upd_product
        }

        const updateAll = (product) => {
            const update_contracts_and_invoices = 1
            setConfirmOptions({
                title: $t("Do you wish to update related contracts and invoices?"),
                confirm_text: $t("Confirm"),
                cancel_text: $t("Cancel"),
                text: $t('Update contract templates, active contracts and open invoices.')
                    + "<br>"
                    + "<br>"
                    + $t('The following fields will be updated')
                    + ":<br>"
                    + "<ul>"
                    + "<li>" + $t("Tax") + " %" + "</li>"
                    + "<li>" + $t("Article") + "</li>"
                    + "<li>" + $t("Project") + "</li>"
                    + "<li>" + $t("Resource") + "</li>"
                    + "<li>" + $t("Cost center") + "</li>"
                    + "<li>" + $t("Custom Value") + "</li>"
                    + "<li>" + $t("Product / service") + "</li>"
                    + "</ul>",
                action: () => onSaveClick(product, update_contracts_and_invoices)
            });
            openConfirmDialog();
        }

        const confirmSave = (product) => {
            const update_contracts_and_invoices = 0
            onSaveClick(product, update_contracts_and_invoices)
        }

        const show_validation_errors = ex => {
            const errors = ex.response.data.errors
            const global_err = [];

            for (let err of Array.from(errors)) {
                if (!err.field) {
                    global_err.push(err.error_message);
                    continue;
                }

                let {field} = err;
                if (["tax_id"].includes(err.field)) {
                    field = "tax_id";
                }

                if (err.index !== undefined) {
                    if (err.parent_index !== undefined) {
                        if (!product.value.periods[err.parent_index]) {
                            Vue.set(product.value.periods, err.parent_index, {
                                prices: []
                            })

                            if (!product.value.periods[err.parent_index].prices[err.index]) {
                                product.value.periods[err.parent_index].prices[err.index] = {}
                                Vue.set(product.value.periods[err.parent_index].prices, err.index, {})
                            }
                        }

                        Vue.set(
                            product.value.periods[err.parent_index].prices[err.index],
                            field + "_error",
                            err.error_message
                        );
                    } else {
                        Vue.set(product.value.periods[err.index], field + "_error", err.error_message);
                    }
                } else {
                    let err_obj = {};
                    err_obj[field + "_error"] = err.error_message;
                    product.value = Object.assign({}, product.value, err_obj);
                }
            }

            if (global_err.length > 0) {
                errorDialog.showError(ex)
            }
        };

        const onSaveClick = async (saveProductData, update_contracts_and_invoices = 0) => {
            await loader.withLoader(async () => {
                try {
                    clearValidationErrors(product.value);

                    const productData = await saveProduct(saveProductData, update_contracts_and_invoices)
                    // console.log('onSaveClick product', productData)

                    ctx.emit("close_modal", null)
                    ctx.emit('saved', productData)
                } catch (ex) {
                    show_validation_errors(ex)
                }
            })
        };

        const onDeleteProductConfirm = async () => {
            try {
                await deleteProduct(props.productId, 1);
                ctx.emit("close_modal", null);
            } catch (ex) {
                setConfirmOptions({
                    title: $t("Warning!"),
                    text: $t("This product is in use. Do you wish to archive it?"),
                    confirm_text: $t("Archive"),
                    cancel_text: $t("Cancel"),
                    action: () => {
                        deleteProduct(props.productId, 0).catch(ex => errorDialog.showError(ex));
                        ctx.emit("close_modal", null);
                    }
                });
                openConfirmDialog();
            }
        };

        const onDeleteProductClick = () => {
            setConfirmOptions({
                title: $t("Notice!"),
                text: $t("Are you sure that you want to delete this product?"),
                confirm_text: $t("Yes"),
                cancel_text: $t("Cancel"),
                action: onDeleteProductConfirm
            });
            openConfirmDialog();
        };

        const onUpdateErrors = (errorsData) => {
            // console.info('product errorsData', errorsData)
            errors.value = errorsData
        }

        return {
            loader,
            canEdit,
            confirmSave,
            product,
            onDeleteProductClick,
            updateAll,
            onUpdateErrors,
            errors,
        }
    }
};
</script>
